/**
 * Substitution-request data layer — chef-initiated ingredient swap
 * requests over a date range. Backs the chef UI's substitution modal
 * (form + ledger) ported from `apps/chefs` in the chefs-on-ordo
 * migration.
 *
 * Mirrors the pattern in `kitchen-orders.ts`. Server primitive is
 * `SubstitutionRequestsManager` (apps/ordo/server/src/managers/).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from './http'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SubstitutionRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED'
export type BuyAmericanStatus = 'DOMESTIC' | 'NON_DOMESTIC_EXCEPTION' | 'NEEDS_REVIEW'
export type BuyAmericanExceptionType =
  | 'FAR_NONAVAILABLE'
  | 'NONAVAILABLE_QUANTITY_OR_QUALITY'
  | 'SIGNIFICANTLY_HIGHER_COST'

export interface SubstitutionRequestSummary {
  id: string
  vendorLocationId: string
  ingredientName: string
  ingredientUnit: string
  replacement: string
  /** Catalog identity of the replacement, when one was picked. Null on
   *  free-text rows and on everything created before catalog matching. */
  substituteIngredientId: string | null
  substituteBrandOptionId: string | null
  reason: string
  buyAmericanStatus: BuyAmericanStatus | null
  domesticCommodityPercent: number | null
  countryOfProduction: string | null
  buyAmericanExceptionType: BuyAmericanExceptionType | null
  buyAmericanExceptionExplanation: string | null
  /** ISO date (YYYY-MM-DD or full timestamp the API returns). */
  startDate: string
  endDate: string
  status: SubstitutionRequestStatus
  rejectionReason: string | null
  submittedByUserId: string
  submittedAt: string
  resolvedAt: string | null
  resolvedByUserId: string | null
}

export interface SubstitutionRequestsResponse {
  requests: SubstitutionRequestSummary[]
}

export interface CreateSubstitutionRequestInput {
  kitchenLocationId: string
  ingredientName: string
  ingredientUnit: string
  replacement: string
  /** Send when the chef picked the replacement from the catalog rather than
   *  typing it — without these, crediting can never follow the substitution. */
  substituteIngredientId?: string | null
  substituteBrandOptionId?: string | null
  reason: string
  buyAmericanStatus?: BuyAmericanStatus | null
  domesticCommodityPercent?: number | null
  countryOfProduction?: string | null
  buyAmericanExceptionType?: BuyAmericanExceptionType | null
  buyAmericanExceptionExplanation?: string | null
  /** YYYY-MM-DD */
  startDate: string
  endDate: string
}

export interface ResolveSubstitutionRequestInput {
  id: string
  status: 'APPROVED' | 'REJECTED'
  rejectionReason?: string
}

interface ListVariables {
  kitchenLocationId: string
  startDate?: string
  endDate?: string
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const substitutionRequestsQueryKey = (vars: ListVariables) =>
  ['substitution-requests', vars.kitchenLocationId, vars.startDate ?? null, vars.endDate ?? null] as const

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

async function fetchSubstitutionRequests(
  vars: ListVariables,
  signal?: AbortSignal,
): Promise<SubstitutionRequestsResponse> {
  return apiFetch<SubstitutionRequestsResponse>('/api/substitution-requests', {
    query: {
      kitchenLocationId: vars.kitchenLocationId,
      startDate: vars.startDate,
      endDate: vars.endDate,
    },
    signal,
  })
}

async function createSubstitutionRequest(
  input: CreateSubstitutionRequestInput,
): Promise<{ request: SubstitutionRequestSummary }> {
  return apiFetch<{ request: SubstitutionRequestSummary }>('/api/substitution-requests', {
    method: 'POST',
    body: input,
  })
}

async function resolveSubstitutionRequest(
  input: ResolveSubstitutionRequestInput,
): Promise<{ request: SubstitutionRequestSummary }> {
  return apiFetch<{ request: SubstitutionRequestSummary }>(`/api/substitution-requests/${input.id}`, {
    method: 'PATCH',
    body: { status: input.status, rejectionReason: input.rejectionReason },
  })
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useSubstitutionRequestsQuery(vars: ListVariables, opts: { enabled?: boolean } = {}) {
  return useQuery<SubstitutionRequestsResponse>({
    queryKey: substitutionRequestsQueryKey(vars),
    queryFn: ({ signal }) => fetchSubstitutionRequests(vars, signal),
    enabled: (opts.enabled ?? true) && Boolean(vars.kitchenLocationId),
    staleTime: 30_000,
    refetchInterval: 30_000,
  })
}

export function useCreateSubstitutionRequestMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createSubstitutionRequest,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['substitution-requests'] })
    },
  })
}

export function useResolveSubstitutionRequestMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: resolveSubstitutionRequest,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['substitution-requests'] })
    },
  })
}
